import type {ChangeEvent, InputHTMLAttributes, ReactNode} from 'react';

import {CheckOutlined} from '@ant-design/icons';
import {useEffect, useId, useRef} from 'react';

import {cn} from '../../helpers/cn';

export type CheckboxChangeEvent = {
  target: {
    checked: boolean;
    value?: string | ReadonlyArray<string> | number;
  };
  stopPropagation: () => void;
  preventDefault: () => void;
};

export type CheckboxProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'onChange' | 'size'
> & {
  checked?: boolean;
  defaultChecked?: boolean;
  indeterminate?: boolean;
  children?: ReactNode;
  onChange?: (e: CheckboxChangeEvent) => void;
};

export function Checkbox({
  checked,
  defaultChecked,
  indeterminate = false,
  disabled,
  children,
  className,
  id,
  onChange,
  ...props
}: CheckboxProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange?.({
      target: {
        checked: event.target.checked,
        value: event.target.value
      },
      stopPropagation: () => event.stopPropagation(),
      preventDefault: () => event.preventDefault()
    });
  };

  return (
    <label
      htmlFor={inputId}
      className={cn(
        'inline-flex cursor-pointer items-start gap-2 font-iransans text-body-sm text-neutral-fg',
        disabled && 'cursor-not-allowed text-disabled-fg',
        className
      )}
    >
      <span className="relative mt-0.5 inline-flex size-4 shrink-0 items-center justify-center">
        <input
          {...props}
          ref={inputRef}
          id={inputId}
          type="checkbox"
          checked={checked}
          defaultChecked={defaultChecked}
          disabled={disabled}
          aria-checked={indeterminate ? 'mixed' : checked}
          onChange={handleChange}
          className="peer absolute inset-0 z-10 m-0 size-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
        />
        <span
          aria-hidden
          className={cn(
            'pointer-events-none relative flex size-4 items-center justify-center rounded-[4px] border border-disabled-border bg-white text-white transition-colors',
            'peer-checked:border-orange peer-checked:bg-orange peer-checked:[&_.ds-checkbox-check]:opacity-100',
            'peer-indeterminate:border-orange peer-indeterminate:[&>.ds-checkbox-dash]:scale-100',
            'peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-orange',
            'peer-hover:border-orange',
            'peer-disabled:border-disabled-border peer-disabled:bg-disabled-surface peer-disabled:text-disabled-fg',
            'peer-disabled:peer-checked:border-disabled-border peer-disabled:peer-checked:bg-disabled-surface'
          )}
        >
          <CheckOutlined
            aria-hidden
            className="ds-checkbox-check text-[10px] opacity-0 transition-opacity"
          />
          <span
            aria-hidden
            className="ds-checkbox-dash pointer-events-none absolute inset-0 m-auto size-2 scale-0 bg-orange transition-transform"
          />
        </span>
      </span>
      {children != null && <span className="min-w-0 leading-5">{children}</span>}
    </label>
  );
}
